import React, { useEffect, useRef, useState } from 'react';
import { Chart } from 'chart.js/auto';

function ROITimelineChart({ processes, contextData }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const [chartSize, setChartSize] = useState('medium');

  useEffect(() => {
    if (!canvasRef.current || !processes || processes.length === 0 || !contextData) {
      return;
    }

    // Destroy existing chart if it exists
    if (chartRef.current) {
      chartRef.current.destroy();
    }

    // Calculate costs
    const professionalServices = contextData?.professional_services_price || 0;
    const bomPrice = contextData?.bom_price || 0;
    const bomLengthMonths = contextData?.bom_length_months || 1;
    const monthlyUsageCosts = contextData?.monthly_usage_costs || 0;
    const costPerAgent = contextData?.cost_per_agent || 25;

    const monthlyBOMCost = bomLengthMonths > 0 ? bomPrice / bomLengthMonths : 0;
    const totalMonthlyCosts = monthlyBOMCost + monthlyUsageCosts;

    // Helper functions for calculations
    const parseTimeToSeconds = (timeStr) => {
      if (!timeStr) return 0;
      const numMatch = timeStr.match(/[\d.]+/);
      if (!numMatch) return 0;
      const value = parseFloat(numMatch[0]);
      const lowerStr = timeStr.toLowerCase();
      if (lowerStr.includes('hour') || lowerStr.includes('hr') || lowerStr.includes('h')) {
        return value * 3600;
      } else if (lowerStr.includes('min') || lowerStr.includes('m')) {
        return value * 60;
      } else if (lowerStr.includes('sec') || lowerStr.includes('s')) {
        return value;
      }
      return value;
    };

    const calculateAutomationWithProbability = (probability, automationPotential) => {
      if (!probability || !automationPotential) return 0;
      const probValue = parseFloat(probability) / 100;
      const autoValue = parseFloat(automationPotential) / 100;
      return probValue * autoValue;
    };

    const calculateTotalInteractions = (numberOfInteractions, automationWProbability) => {
      if (!numberOfInteractions || !automationWProbability) return 0;
      const interactions = parseFloat(numberOfInteractions);
      return interactions * automationWProbability;
    };

    const calculateTotalTimeSaved = (totalInteractions, avgTimePerInteraction) => {
      if (!totalInteractions || !avgTimePerInteraction) return 0;
      const timeInSeconds = parseTimeToSeconds(avgTimePerInteraction);
      return totalInteractions * timeInSeconds;
    };

    // Calculate total savings for each case
    const cases = ['Conservative', 'Likely', 'Optimistic'];
    const defaultProbabilities = {
      'Conservative': '65%',
      'Likely': '80%',
      'Optimistic': '95%'
    };
    const defaultValues = {
      automation_potential: '80%',
      number_of_interactions: '10000',
      avg_time_per_interaction: '30 sec'
    };

    const savingsByCase = {};
    cases.forEach((caseType) => {
      savingsByCase[caseType] = processes.reduce((sum, process) => {
        const caseData = process.roi_cases?.[caseType] || {};

        const automationWProbability = calculateAutomationWithProbability(
          caseData.probability || defaultProbabilities[caseType],
          caseData.automation_potential || defaultValues.automation_potential
        );
        const totalInteractions = calculateTotalInteractions(
          caseData.number_of_interactions || defaultValues.number_of_interactions,
          automationWProbability
        );
        const totalTimeSaved = calculateTotalTimeSaved(
          totalInteractions,
          caseData.avg_time_per_interaction || defaultValues.avg_time_per_interaction
        );

        const timeInHours = parseFloat(totalTimeSaved) / 3600;
        const savings = timeInHours * costPerAgent;

        return sum + savings;
      }, 0);
    });

    // Calculate net monthly savings and months to payoff
    const roiData = {};
    cases.forEach((caseType) => {
      const netMonthlySavings = savingsByCase[caseType] - totalMonthlyCosts;
      const monthsToPayoff = netMonthlySavings > 0
        ? professionalServices / netMonthlySavings
        : Infinity;

      roiData[caseType] = {
        netMonthlySavings,
        monthsToPayoff: monthsToPayoff < 100 ? monthsToPayoff : 100
      };
    });

    // Generate chart data for up to 36 months or longest payoff + 6 months
    const maxMonths = Math.min(
      Math.max(
        ...cases.map(c => roiData[c].monthsToPayoff < 100 ? roiData[c].monthsToPayoff : 0)
      ) + 6,
      36
    );

    const months = Array.from({ length: Math.ceil(maxMonths) + 1 }, (_, i) => i);

    // Calculate cumulative values for each case
    const datasets = cases.map((caseType, index) => {
      const colors = {
        'Conservative': { bg: 'rgba(239, 68, 68, 0.2)', border: 'rgba(239, 68, 68, 1)' },
        'Likely': { bg: 'rgba(234, 179, 8, 0.2)', border: 'rgba(234, 179, 8, 1)' },
        'Optimistic': { bg: 'rgba(34, 197, 94, 0.2)', border: 'rgba(34, 197, 94, 1)' }
      };

      const data = months.map(month => {
        if (month === 0) return -professionalServices;

        const cumulativeSavings = roiData[caseType].netMonthlySavings * month;
        return cumulativeSavings - professionalServices;
      });

      return {
        label: caseType,
        data: data,
        borderColor: colors[caseType].border,
        backgroundColor: colors[caseType].bg,
        borderWidth: 3,
        fill: false,
        tension: 0.1,
        pointRadius: 0,
        pointHoverRadius: 6
      };
    });

    const ctx = canvasRef.current.getContext('2d');

    // Create chart
    chartRef.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: months,
        datasets: datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 2,
        plugins: {
          title: {
            display: true,
            text: 'ROI Timeline: Cumulative Net Savings Over Time',
            font: { size: 18, weight: 'bold' }
          },
          legend: {
            display: true,
            position: 'top'
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                const value = context.parsed.y;
                return `${context.dataset.label}: $${value.toFixed(2)}`;
              }
            }
          }
        },
        scales: {
          x: {
            title: { display: true, text: 'Months', font: { size: 14, weight: 'bold' } },
            ticks: {
              stepSize: 3
            }
          },
          y: {
            title: { display: true, text: 'Cumulative Net Savings ($)', font: { size: 14, weight: 'bold' } },
            ticks: {
              callback: function(value) {
                return '$' + value.toLocaleString();
              }
            },
            grid: {
              color: function(context) {
                // Make the zero line more prominent
                if (context.tick.value === 0) {
                  return 'rgba(0, 0, 0, 0.3)';
                }
                return 'rgba(0, 0, 0, 0.1)';
              },
              lineWidth: function(context) {
                if (context.tick.value === 0) {
                  return 2;
                }
                return 1;
              }
            }
          }
        }
      }
    });

    // Cleanup on unmount
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
      }
    };
  }, [processes, contextData, chartSize]);

  if (!processes || processes.length === 0) {
    return (
      <div className="bg-gray-100 p-8 rounded-lg text-center text-gray-600">
        No processes available to display ROI chart
      </div>
    );
  }

  if (!contextData) {
    return (
      <div className="bg-gray-100 p-8 rounded-lg text-center text-gray-600">
        Please configure customer context to display ROI chart
      </div>
    );
  }

  const sizeOptions = {
    small: '400px',
    medium: '700px',
    large: '1000px'
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="mb-4 flex items-center justify-center gap-2">
        <span className="text-sm text-gray-600 font-medium">Chart Size:</span>
        <button
          onClick={() => setChartSize('small')}
          className={`px-3 py-1 text-sm rounded ${
            chartSize === 'small'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Small
        </button>
        <button
          onClick={() => setChartSize('medium')}
          className={`px-3 py-1 text-sm rounded ${
            chartSize === 'medium'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Medium
        </button>
        <button
          onClick={() => setChartSize('large')}
          className={`px-3 py-1 text-sm rounded ${
            chartSize === 'large'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Large
        </button>
      </div>
      <div style={{ maxWidth: sizeOptions[chartSize], margin: '0 auto' }}>
        <canvas ref={canvasRef}></canvas>
      </div>
      <div className="mt-4 p-3 bg-blue-50 rounded text-sm text-gray-700">
        <strong>Chart Explanation:</strong> This chart shows cumulative net savings over time for each scenario.
        The point where each line crosses $0 represents the payoff period (when Professional Services costs are recovered).
      </div>
    </div>
  );
}

export default ROITimelineChart;
